import { DateTime } from 'luxon';
import { useMemo } from 'react';

import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import { MonoSpan } from 'dashboard/components/Typography';
import TableLayout, {
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import {
  REPORT_TEMPLATE_TYPE_TO_LABEL,
  ReportTemplate,
  ReportTemplateFilterOption,
  ReportTemplateStatus,
} from 'dashboard/models/report-template';
import Sticker from 'design-system/components/Sticker';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import usePermission from 'lib/hooks/usePermission';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

interface ReportTemplatesTableProps {
  reportTemplates: ReportTemplate[];
  reportTemplateFilterOptions: ReportTemplateFilterOption[];
  sortParams: SortParams;
  hasUsedFilters: boolean;
  isLoading: boolean;
  handleSortUpdate: (sortParams: SortParams) => void;
  archiveReportTemplate: (reportTemplate: ReportTemplate) => Promise<void>;
}

const ReportTemplatesTable = ({
  reportTemplates,
  reportTemplateFilterOptions,
  sortParams,
  hasUsedFilters,
  isLoading,
  handleSortUpdate,
}: ReportTemplatesTableProps) => {
  const handleColumnHeaderClick = (columnName: string) => {
    handleSortUpdate({
      sort: columnName,
      shouldSortDescending:
        columnName === sortParams.sort ? !sortParams.shouldSortDescending : true,
    });
  };

  /*
   * Temporary filter to exclude integration templates until full FE support is implemented
   */
  const isfilteredTemplate = (ttype: ApiModel) => {
    return [
      ApiModel.ReportTemplateClearbitBusinessLookup,
      ApiModel.ReportTemplateCoinbaseCheckCryptoRisk,
      ApiModel.ReportTemplateNovaCreditCashAtlas,
      ApiModel.ReportTemplateNovaCreditCreditPassport,
    ].includes(ttype);
  };

  const hasReportTemplateViewPermission = usePermission(Permission.ReportTemplateView);

  const reportTemplateRows = useMemo(() => {
    if (!hasReportTemplateViewPermission) {
      return reportTemplateFilterOptions
        .filter((rtfo) => !rtfo.archived && !isfilteredTemplate(rtfo.type))
        .map((reportTemplateFilterOption) => {
          const { name, 'updated-at': updatedAt } = reportTemplateFilterOption;
          return (
            <TableTR key={reportTemplateFilterOption.id}>
              <TableTD>{name}</TableTD>
              <TableTD>
                <HoverCopyableField
                  copyValue={reportTemplateFilterOption.id}
                  confirmationText='Copied Report Template ID'
                >
                  <MonoSpan>{reportTemplateFilterOption.id}</MonoSpan>
                </HoverCopyableField>
              </TableTD>
              <TableTD>{REPORT_TEMPLATE_TYPE_TO_LABEL[reportTemplateFilterOption.type]}</TableTD>
              <TableTD>
                <StatusSticker
                  isActive={reportTemplateFilterOption.status === ReportTemplateStatus.Active}
                />
              </TableTD>
              <TableTD>{DateTime.fromISO(updatedAt).toFormat(DATE_AND_TIME_FORMAT)}</TableTD>
            </TableTR>
          );
        });
    } else {
      return reportTemplates
        .filter((rt) => !rt.attributes.archived && !isfilteredTemplate(rt.type))
        .map((reportTemplate) => {
          const { name, 'updated-at': updatedAt } = reportTemplate.attributes;
          return (
            <TableLinkTR
              to={`/dashboard/report-templates/${reportTemplate.id}`}
              id={`report-templates-table__row--${reportTemplate.id}`}
              key={reportTemplate.id}
            >
              <TableTD>{name}</TableTD>
              <TableTD>
                <HoverCopyableField
                  copyValue={reportTemplate.id}
                  confirmationText='Copied Report Template ID'
                >
                  <MonoSpan>{reportTemplate.id}</MonoSpan>
                </HoverCopyableField>
              </TableTD>
              <TableTD>{REPORT_TEMPLATE_TYPE_TO_LABEL[reportTemplate.type]}</TableTD>
              <TableTD>
                <StatusSticker
                  isActive={reportTemplate?.attributes?.status === ReportTemplateStatus.Active}
                />
              </TableTD>
              <TableTD>{DateTime.fromISO(updatedAt).toFormat(DATE_AND_TIME_FORMAT)}</TableTD>
            </TableLinkTR>
          );
        });
    }
  }, [hasReportTemplateViewPermission, reportTemplateFilterOptions, reportTemplates]);

  return (
    <>
      <TableLayout
        isLoading={isLoading}
        showHeader={true}
        isEmpty={
          !hasReportTemplateViewPermission
            ? reportTemplateFilterOptions.length === 0
            : reportTemplates.length === 0
        }
        nullState={
          <TableNullState hasUsedFilters={hasUsedFilters}>
            <h3>Looks like there aren&#39;t any report templates yet.</h3>
            <p>Create a report template to get started.</p>
          </TableNullState>
        }
        headerTR={
          <>
            <TableTH>Name</TableTH>
            <TableTH>ID</TableTH>
            <TableTH>Type</TableTH>
            <TableTH>Status</TableTH>
            <TableTH
              onClick={() => handleColumnHeaderClick('updated_at')}
              canSort={true}
              isSorted={sortParams.sort === 'updated_at'}
              isDescending={sortParams.shouldSortDescending}
              id='report-templates-table__updated-at-header'
            >
              {`Last updated at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
            </TableTH>
          </>
        }
      >
        {reportTemplateRows}
      </TableLayout>
    </>
  );
};

const StatusSticker = ({ isActive }: { isActive: boolean }) => {
  return (
    <>{isActive ? <Sticker variant='success'>Active</Sticker> : <Sticker>Inactive</Sticker>}</>
  );
};

export default ReportTemplatesTable;
